import {
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Modal,
    Box,
    Typography
} from '@mui/material'
import { useTheme } from "@mui/material/styles"
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import useApiError from 'packages/novous_web/src/api/useApiError';
import AlertToast from 'packages/novous_web/src/common/components/Alert';
import Button from 'packages/novous_web/src/common/components/Button';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import React, { useEffect, useState } from 'react'

const BannerDataTable = ({ bannerData, refreshData }: { bannerData: any, refreshData: () => void; }) => {
    const [tableData, setTableData] = useState<any[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
    const [intendedActiveState, setIntendedActiveState] = useState<boolean>(false);
    const [toast, setToast] = useState({ open: false, message: '', type: 'error' });

    const theme = useTheme();
    const tableHeaderBg = theme.palette.mode === "dark" ? "#2D2D2D" : "#F0EFF8";    

    const {
        data: bannerDataUpdate,
        error: bannerDataUpdateError,
        loading: bannerDataUpdateLoading,
        fetchData: fetchbannerDataUpdateData,
    } = useApiError<any>(`${API_ENDPOINTS.users.USER_BANNER_UPDATE}`, "POST");

    useEffect(() => {
        if (bannerData) {
            setTableData(bannerData);
        }
    }, [bannerData]);

    const formatDateTime = (dateStr: string) => {
        const fixedStr = dateStr.replace(":", "T");
        const date = new Date(fixedStr);
        if (isNaN(date.getTime())) return "Invalid Date";

        const day = date.getDate();
        const month = date.toLocaleString("default", { month: "long" });
        const year = date.getFullYear();
        const formattedTime = date.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });

        const getOrdinal = (n: number) => {
            if (n > 3 && n < 21) return "th";
            switch (n % 10) {
                case 1: return "st";
                case 2: return "nd";
                case 3: return "rd";
                default: return "th";
            }
        };

        return `${day}${getOrdinal(day)} ${month} ${year}, ${formattedTime}`;
    };

    const handleToggleClick = (index: number) => {
        const currentState = tableData[index].bannerActive;
        setSelectedRowIndex(index);
        setIntendedActiveState(!currentState);
        setDialogOpen(true);
    };

    const handleConfirmToggle = async () => {
        if (selectedRowIndex === null) return;

        const updatedData = [...tableData];
        updatedData[selectedRowIndex] = {
            ...updatedData[selectedRowIndex],
            bannerActive: intendedActiveState
        };

        setTableData(updatedData);
        setDialogOpen(false);

        const bannerId = updatedData[selectedRowIndex].bannerId;
        try {
            const response = await fetchbannerDataUpdateData({
                bannerId: bannerId,
                bannerActive: intendedActiveState
            });
            if(response?.status === 200 || response?.status === 201) {
                setToast({ open: true, message: response.message, type: 'success' });
                refreshData();
            }
        } catch {
            setToast({ open: true, message: `Error to ${intendedActiveState} this banner`, type: 'error' });
        } finally {
            setSelectedRowIndex(null);
        }
    };
    
    useEffect(() => {
        if (bannerDataUpdateError) {
            setToast({
                open: true,
                message: bannerDataUpdateError as string,
                type: "error",
            })
        }
    }, [bannerDataUpdateError])

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedRowIndex(null);
    };

    return (
        <>
            <TableContainer component={Paper} sx={{ maxHeight: "50vh" }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: tableHeaderBg }}>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: tableHeaderBg }}>Banner Id</TableCell>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: tableHeaderBg }}>Banner Title</TableCell>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: tableHeaderBg }}>Banner Message</TableCell>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: tableHeaderBg }}>Banner type</TableCell>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: tableHeaderBg }}>Start Date & Time</TableCell>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: tableHeaderBg }}>End Date & Time</TableCell>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: tableHeaderBg }}>Banner Active</TableCell>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: tableHeaderBg }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {tableData?.map((row: any, index: number) => (
                            <TableRow key={index}>
                                <TableCell sx={{ fontWeight: 400, fontSize: "14px" }}>{row?.bannerId}</TableCell>
                                <TableCell sx={{ fontWeight: 400, fontSize: "14px" }}>{row?.bannerTitle}</TableCell>
                                <TableCell sx={{ fontWeight: 400, fontSize: "14px" }}>{row?.message}</TableCell>
                                <TableCell sx={{ fontWeight: 400, fontSize: "14px" }}>{row?.bannerType}</TableCell>
                                <TableCell sx={{ fontWeight: 400, fontSize: "14px" }}>{formatDateTime(row?.startDate)}</TableCell>
                                <TableCell sx={{ fontWeight: 400, fontSize: "14px" }}>{formatDateTime(row?.endDate)}</TableCell>
                                <TableCell sx={{ fontWeight: 400, fontSize: "14px" }}>{row?.bannerActive ? "Active" : "Inactive"}</TableCell>
                                <TableCell sx={{ fontWeight: 400, fontSize: "14px" }}>
                                    <Switch
                                        checked={row.bannerActive}
                                        onChange={() => handleToggleClick(index)}
                                        color="primary"
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Modal
                open={dialogOpen}
                onClose={handleDialogClose}
                aria-labelledby="download-modal-title"
                aria-describedby="download-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '440px',
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 4,
                    }}
                >
                    <Typography variant="h6" sx={{ color: (theme) => theme.palette.text.primary }}>
                        Confirm Toggel
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: (theme) => theme.palette.text.secondary,
                            fontSize: '14px',
                            lineHeight: '20px'
                        }}
                    >
                        Are you sure you want to {intendedActiveState ? "enable" : "disable"} this banner?
                    </Typography>
                    <div className="flex gap-2 justify-between mt-8">

                        <CustomIconButton text="Cancel"
                            background="transparent" outline={true} padding="0px"
                            variant="outlined"
                            onClick={handleDialogClose}
                        />

                        <Button
                            text='Confirm'
                            onClick={handleConfirmToggle}
                            fullWidth={false}
                        />
                    </div>
                </Box>
            </Modal>
            <AlertToast
                open={toast.open}
                message={toast.message}
                onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                type={toast.type as any}
            />
        </>
    )
}

export default BannerDataTable;
